import { useMemo } from 'react';
import endpoints from '@services/api/endpoints';
import useFetch from '@services/api/useFetch';
import { replaceUrlParams } from '@utils/string-util';

import type {
  CateringLeadFeedbackRequest,
  CateringLeadItem,
  CateringLeadPersonalized,
  CateringLeadSearchParams,
  CateringLeadSearchResults,
  CateringLeadTicket,
} from '../../../types/events/catering-leads';

export const useCateringLeadsApi = () => {
  const api = useFetch();
  return useMemo(() => {
    return {
      async getPersonalized(id: string): Promise<CateringLeadPersonalized> {
        return api.get(
          replaceUrlParams(endpoints.cateringLeads.getPersonalized, {
            id,
          })
        );
      },
      async search(
        organizationId: string,
        params: Partial<CateringLeadSearchParams>
      ): Promise<CateringLeadSearchResults> {
        return api.get(endpoints.cateringLeads.search, {
          organizationId,
          ...params,
        });
      },
      async isRunningTicket(
        organizationId: string
      ): Promise<CateringLeadTicket> {
        return api.get(
          replaceUrlParams(endpoints.cateringLeads.isRunning, {
            organizationId,
          }),
          {
            organizationId,
          }
        );
      },
      async togglePin(id: string, pinned: boolean): Promise<CateringLeadItem> {
        return api.patch(
          replaceUrlParams(endpoints.cateringLeads.togglePin, {
            id,
            pinned,
          })
        );
      },
      async addFeedback(
        id: string,
        params: CateringLeadFeedbackRequest
      ): Promise<CateringLeadPersonalized> {
        return api.patch(
          replaceUrlParams(endpoints.cateringLeads.updateFeedback, {
            id,
          }),
          params
        );
      },
      async updateNotes(
        id: string,
        notes: string
      ): Promise<CateringLeadPersonalized> {
        return api.patch(
          replaceUrlParams(endpoints.cateringLeads.updateNotes, {
            id,
          }),
          {
            notes,
          }
        );
      },
    };
  }, [api]);
};

export const cateringLeadsApiKeys = {
  all: ['catering-leads'] as const,
  getPersonalized: (id: string) =>
    [...cateringLeadsApiKeys.all, 'get-personalized', id] as const,
  isRunningTicket: (orgId: string) =>
    [...cateringLeadsApiKeys.all, 'is-running-ticket', orgId] as const,
  search: (orgId: string, params?: Partial<CateringLeadSearchParams>) =>
    [...cateringLeadsApiKeys.all, 'catering-leads-search', orgId, params].filter(
      Boolean
    ),
};
